import { dismissFloorFilter } from "../../search/domains/store/actionCreators";
import {
  ANALYTICS_CLEAR_ALL_REFINEMENTS,
  ANALYTICS_CLEAR_REFINEMENT,
  ANALYTICS_REFINEMENTS_SELECT,
  ANALYTICS_REFINEMENTS_SORT,
  ANALYTICS_REFINEMENTS_TOGGLED,
  ANALYTICS_SELECT_ALL,
} from "../../analytics/constants";
import { reportFacetEvent } from "../analytics";
import {
  clearAllFacets,
  clearFacet,
  resetFacetDraft,
  setFacetRange,
  setFacetSort,
  toggleFacetValue,
} from "../edits";
import { applyFacetDraft } from "../commit";
import {
  commitWillFollow,
  EDIT_SOURCE,
  resolveCommitPolicy,
  willSearchFollow,
} from "../commitPolicy";
import { selectVisibleFacets, selectPriceBounds } from "../selectors";

export { default as done } from "./done";
export { EDIT_SOURCE } from "../commitPolicy";

const FLOOR_FACET_ID = "floor";

const getFacet = (state, facetId) =>
  selectVisibleFacets(state).find(({ id }) => id === facetId);

const getSelectedValues = (facet) =>
  (facet.facetValues || []).filter(({ isSelected }) => isSelected);

export function applyFacets(options = {}) {
  const { keepSelections = true } = options;

  return (dispatch) => {
    dispatch(applyFacetDraft());

    if (!keepSelections) {
      dispatch(resetFacetDraft());
    }
  };
}

const resolveToggles = (state, toggles) =>
  toggles
    .map(({ facetId, valueId, isSelected }) => {
      const facet = getFacet(state, facetId);
      const facetValue = facet?.facetValues?.find(({ id }) => id === valueId);

      return facet && facetValue ? { facet, facetValue, isSelected } : null;
    })
    .filter(Boolean);

const dismissFloorDeselections = (dispatch, toggles) =>
  toggles
    .filter(
      ({ facetId, isSelected }) =>
        facetId === FLOOR_FACET_ID && isSelected === false,
    )
    .forEach(({ valueId }) => dispatch(dismissFloorFilter(String(valueId))));

// Outside-panel selections are automatic floor filters, not shopper events.
export function toggleFacetValues(toggles, source) {
  return (dispatch, getState) => {
    const normalized = (Array.isArray(toggles) ? toggles : [toggles]).map(
      ({ facet, facetValue, facetId, valueId, isSelected }) => ({
        facetId: facetId ?? facet?.id,
        valueId: valueId ?? facetValue?.id,
        isSelected,
      }),
    );
    const state = getState();
    const inPanel = source === EDIT_SOURCE.VALUE_IN_PANEL;
    const reportable = inPanel
      ? normalized
      : normalized.filter(({ isSelected }) => isSelected === false);

    dismissFloorDeselections(dispatch, normalized);

    normalized.forEach((toggle) => dispatch(toggleFacetValue(toggle, source)));

    if (reportable.length > 0) {
      reportFacetEvent(
        dispatch,
        willSearchFollow(source, state),
        ANALYTICS_REFINEMENTS_TOGGLED,
        resolveToggles(state, reportable),
      );
    }
  };
}

export const changeSort =
  ({ type, name }) =>
  (dispatch) => {
    dispatch(setFacetSort(type, EDIT_SOURCE.ALWAYS_COMMITS));
    reportFacetEvent(dispatch, true, ANALYTICS_REFINEMENTS_SORT, {
      type,
      name,
    });
  };

export function clearFacetValues(facetId, source) {
  return (dispatch, getState) => {
    const state = getState();
    const facet = getFacet(state, facetId);

    if (!facet) {
      return;
    }

    const clearedFacetValues = getSelectedValues(facet);

    dispatch(clearFacet(facetId, source));
    reportFacetEvent(
      dispatch,
      willSearchFollow(source, state),
      ANALYTICS_CLEAR_REFINEMENT,
      { facet, clearedFacetValues },
    );
  };
}

export function clearAll() {
  return (dispatch, getState) => {
    reportFacetEvent(dispatch, true, ANALYTICS_CLEAR_ALL_REFINEMENTS, {
      facets: selectVisibleFacets(getState()),
    });
    dispatch(clearAllFacets(undefined, EDIT_SOURCE.ALWAYS_COMMITS));
  };
}

export function selectAll(facetId) {
  return (dispatch, getState) => {
    const state = getState();
    const facet = getFacet(state, facetId);

    if (!facet) {
      return;
    }

    facet.facetValues
      .filter(({ count }) => count)
      .forEach(({ id }) =>
        dispatch(
          toggleFacetValue(
            { facetId, valueId: id, isSelected: true },
            EDIT_SOURCE.ACTION_IN_PANEL,
          ),
        ),
      );

    reportFacetEvent(
      dispatch,
      willSearchFollow(EDIT_SOURCE.ACTION_IN_PANEL, state),
      ANALYTICS_SELECT_ALL,
      facet,
    );
  };
}

export function changeRangeFacet(facetId, value) {
  return (dispatch, getState) => {
    const { min, max } = selectPriceBounds(getState());
    const selectedMin = value?.min ?? value?.leftIndicator;
    const selectedMax = value?.max ?? value?.rightIndicator;
    const coversEverything = selectedMin === min && selectedMax === max;

    dispatch(
      coversEverything
        ? clearFacet(facetId, EDIT_SOURCE.NEVER_COMMITS)
        : setFacetRange({ facetId, range: value }, EDIT_SOURCE.NEVER_COMMITS),
    );
  };
}

export function applyRangeFacet(facet) {
  return (dispatch, getState) => {
    const state = getState();
    const policy = resolveCommitPolicy(EDIT_SOURCE.ACTION_IN_PANEL, state);

    if (commitWillFollow(policy)) {
      dispatch(applyFacets());
    }

    reportFacetEvent(
      dispatch,
      willSearchFollow(EDIT_SOURCE.ACTION_IN_PANEL, state),
      ANALYTICS_REFINEMENTS_SELECT,
      { facet },
    );
  };
}

export function clearCanonicalRangeFacet() {
  return (dispatch) =>
    dispatch(clearFacet("currentprice", EDIT_SOURCE.NEVER_COMMITS));
}
